import React, { useMemo, useState } from 'react';
import { useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Architecture as BlueprintIcon,
} from '@mui/icons-material';
import { getApiUrl } from '../utils/apiConfig';

const REQUEST_STEPS = ['Kontakt', 'Ihr Vorhaben', 'Termin & Absenden'];

const formatGermanDate = (value) => {
  if (!value) return '-';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : String(value);
};

// Wandelt einen Miteigentumsanteil (z. B. "125/1000") in einen Faktor (0..1) um.
const parseMiteigentumsanteil = (value) => {
  if (!value) return null;
  const match = String(value).match(/(\d+(?:[.,]\d+)?)\s*\/\s*(\d+(?:[.,]\d+)?)/);
  if (!match) return null;
  const numerator = parseFloat(match[1].replace(',', '.'));
  const denominator = parseFloat(match[2].replace(',', '.'));
  if (!denominator || isNaN(numerator) || isNaN(denominator)) return null;
  return numerator / denominator;
};

// Formatiert eine Zahl mit deutschem Tausenderpunkt und optionaler Nachkommastelle.
const formatGermanNumber = (value, maxFractionDigits = 0) => {
  const num = Number(value);
  if (isNaN(num)) return null;
  return num.toLocaleString('de-DE', { maximumFractionDigits: maxFractionDigits });
};

const defaultForm = {
  name: '',
  email: '',
  telefon: '',
  finanzierung: '',
  absicht: '',
  zeithorizont: '',
  nachricht: '',
  dsgvo_consent: false,
  maklerbedingungen_consent: false,
  vorzeitiger_beginn_consent: false,
  widerrufsbelehrung_consent: false,
  finanzierungscheck_confirmed: false,
  website: '',
};

const PropertyLandingPage = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [property, setProperty] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [viewingSlots, setViewingSlots] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [errors, setErrors] = useState([]);
  const [submitResult, setSubmitResult] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = await axios.get(getApiUrl(`/api/public/properties/${slug}`));
        if (!active) return;
        const loadedProperty = response.data.property;
        setProperty(loadedProperty);
        setDocuments(response.data.documents || []);
        setViewingSlots(response.data.viewingSlots || []);
        setForm((prev) => ({
          ...prev,
          nachricht: prev.nachricht
            || `Guten Tag,\n\nich interessiere mich für "${loadedProperty?.titel || 'Ihr Objekt'}"${loadedProperty?.adresse ? ` (${loadedProperty.adresse})` : ''} und möchte das vollständige Exposé sowie weitere Informationen erhalten.\n\nVielen Dank im Voraus.`,
        }));
      } catch (error) {
        if (!active) return;
        setErrors(['Objekt konnte nicht geladen werden. Bitte versuchen Sie es später erneut.']);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [slug]);

  const visibleFeatures = useMemo(() => {
    if (!property?.ausstattung) return [];
    return Array.isArray(property.ausstattung) ? property.ausstattung : [];
  }, [property]);

  // Kombiniert Grundstücksfläche (gesamt) und Miteigentumsanteil zu einer
  // käuferfreundlichen Aussage über den rechnerischen Grundstücksanteil.
  const grundstueck = useMemo(() => {
    const gesamt = property?.grundstuecksflaeche != null && property.grundstuecksflaeche !== ''
      ? Number(property.grundstuecksflaeche)
      : null;
    const anteilText = property?.miteigentumsanteil || null;
    const faktor = parseMiteigentumsanteil(anteilText);
    const anteilFlaeche = (gesamt != null && !isNaN(gesamt) && faktor != null)
      ? gesamt * faktor
      : null;
    return {
      gesamt: gesamt != null && !isNaN(gesamt) ? gesamt : null,
      anteilText,
      anteilProzent: faktor != null ? faktor * 100 : null,
      anteilFlaeche,
    };
  }, [property]);

  const images = useMemo(() => {
    if (!property?.fotos) return [];
    return Array.isArray(property.fotos) ? property.fotos : [];
  }, [property]);

  const orderedImages = useMemo(() => {
    if (!images.length) return [];
    if (!property?.cover_image_url) return images;

    const cover = images.find((img) => img?.url === property.cover_image_url);
    if (!cover) return images;

    const rest = images.filter((img) => img?.url !== property.cover_image_url);
    return [cover, ...rest];
  }, [images, property]);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapQuery = encodeURIComponent(property?.adresse || '');
  const mapEmbedUrl = googleMapsApiKey
    ? `https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${mapQuery}`
    : `https://www.google.com/maps?q=${mapQuery}&output=embed`;

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [property?.slug, orderedImages.length]);

  const showImage = (index) => {
    const count = orderedImages.length;
    if (count === 0) return;
    const nextIndex = ((index % count) + count) % count;
    setSelectedImageIndex(nextIndex);
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSlot = (slotId) => {
    setSelectedSlots((prev) => {
      if (prev.includes(slotId)) return prev.filter((id) => id !== slotId);
      if (prev.length >= 2) return prev;
      return [...prev, slotId];
    });
  };

  const validateStep = (step) => {
    const nextErrors = [];
    if (step === 0) {
      if (!form.name.trim()) nextErrors.push('Bitte Namen eingeben.');
      if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) nextErrors.push('Bitte eine gültige E-Mail-Adresse eingeben.');
    }
    if (step === 1) {
      if (!form.absicht) nextErrors.push('Bitte Kaufabsicht auswählen.');
      if (!form.finanzierung) nextErrors.push('Bitte Finanzierung auswählen.');
      if (!form.zeithorizont) nextErrors.push('Bitte Zeithorizont auswählen.');
      if (!form.finanzierungscheck_confirmed) nextErrors.push('Bitte bestätigen Sie die Finanzierungs-Checkliste.');
    }
    setErrors(nextErrors);
    return nextErrors.length === 0;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((prev) => Math.min(prev + 1, REQUEST_STEPS.length - 1));
  };

  const handleBack = () => {
    setErrors([]);
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const clientValidate = () => {
    const nextErrors = [];
    if (!form.name.trim()) nextErrors.push('Bitte Namen eingeben.');
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) nextErrors.push('Bitte eine gültige E-Mail-Adresse eingeben.');
    if (!form.finanzierung) nextErrors.push('Bitte Finanzierung auswählen.');
    if (!form.absicht) nextErrors.push('Bitte Kaufabsicht auswählen.');
    if (!form.zeithorizont) nextErrors.push('Bitte Zeithorizont auswählen.');
    if (!form.vorzeitiger_beginn_consent) nextErrors.push('Bitte bestätigen Sie den vorzeitigen Beginn der Maklertätigkeit.');
    if (!form.widerrufsbelehrung_consent) nextErrors.push('Bitte bestätigen Sie den Erhalt der Widerrufsbelehrung.');
    if (!form.finanzierungscheck_confirmed) nextErrors.push('Bitte bestätigen Sie die Finanzierungs-Checkliste.');
    if (!form.maklerbedingungen_consent) nextErrors.push('Bitte akzeptieren Sie die Maklervertragsbedingungen.');
    if (!form.dsgvo_consent) nextErrors.push('Bitte der DSGVO-Einwilligung zustimmen.');
    if (selectedSlots.length > 2) nextErrors.push('Bitte maximal 2 Wunschtermine auswählen.');

    setErrors(nextErrors);
    return nextErrors.length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitResult(null);

    if (!clientValidate()) return;

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        requested_slot_ids: selectedSlots,
      };

      const response = await axios.post(getApiUrl(`/api/public/properties/${slug}/leads`), payload);
      setSubmitResult(response.data);
      setErrors([]);
      setForm(defaultForm);
      setSelectedSlots([]);
      setActiveStep(0);
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        setErrors(apiErrors);
      } else {
        setErrors([error?.response?.data?.message || 'Anfrage konnte nicht gespeichert werden.']);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!property) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">Objekt nicht gefunden.</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {property.titel}
                </Typography>
                {property.untertitel && (
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>{property.untertitel}</Typography>
                )}
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>{property.adresse}</Typography>
                <Typography variant="h5" sx={{ color: '#1a365d', fontWeight: 800 }}>
                  Kaufpreis: {property.kaufpreis ? `${Number(property.kaufpreis).toLocaleString('de-DE')} EUR` : 'auf Anfrage'}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {property.vermarktungsart && <Chip size="small" label={property.vermarktungsart} />}
                  {property.objektart && <Chip size="small" label={property.objektart} variant="outlined" />}
                  {property.objektunterart && <Chip size="small" label={property.objektunterart} variant="outlined" />}
                  {property.zustand && <Chip size="small" label={property.zustand} variant="outlined" />}
                  {property.lagekategorie && <Chip size="small" label={property.lagekategorie} variant="outlined" />}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {orderedImages.length > 0 && (
            <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: { xs: 280, sm: 400, md: 540 },
                  bgcolor: '#0f172a',
                }}
              >
                <Box
                  component="img"
                  src={orderedImages[selectedImageIndex]?.url}
                  alt={orderedImages[selectedImageIndex]?.caption || 'Objektbild'}
                  onClick={() => setLightboxOpen(true)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    cursor: 'zoom-in',
                  }}
                />

                <IconButton
                  onClick={() => setLightboxOpen(true)}
                  aria-label="Bild vergrößern"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    bgcolor: 'rgba(15,23,42,0.55)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(15,23,42,0.8)' },
                  }}
                >
                  <ZoomInIcon />
                </IconButton>

                {orderedImages.length > 1 && (
                  <>
                    <IconButton
                      onClick={() => showImage(selectedImageIndex - 1)}
                      aria-label="Vorheriges Bild"
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 12,
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(15,23,42,0.55)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(15,23,42,0.8)' },
                      }}
                    >
                      <NavigateBeforeIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => showImage(selectedImageIndex + 1)}
                      aria-label="Nächstes Bild"
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 12,
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(15,23,42,0.55)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(15,23,42,0.8)' },
                      }}
                    >
                      <NavigateNextIcon />
                    </IconButton>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        right: 12,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 2,
                        bgcolor: 'rgba(15,23,42,0.65)',
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {selectedImageIndex + 1} / {orderedImages.length}
                    </Box>
                  </>
                )}
              </Box>
              {orderedImages.length > 1 && (
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { height: 6 } }}
                  >
                    {orderedImages.map((image, index) => (
                      <Box
                        key={`${image.url}-${index}`}
                        onClick={() => setSelectedImageIndex(index)}
                        sx={{
                          flex: '0 0 auto',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          border: selectedImageIndex === index ? '2px solid #0ea5e9' : '1px solid #cbd5e1',
                          opacity: selectedImageIndex === index ? 1 : 0.75,
                          cursor: 'pointer',
                          transition: 'opacity 0.15s ease',
                          '&:hover': { opacity: 1 },
                        }}
                      >
                        <Box
                          component="img"
                          src={image.url}
                          alt={image.caption || `Objektbild ${index + 1}`}
                          sx={{ width: 110, height: 78, objectFit: 'cover', display: 'block' }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              )}
            </Card>
          )}

          <Dialog
            open={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            fullScreen
            PaperProps={{ sx: { bgcolor: 'rgba(2,6,23,0.97)' } }}
          >
            <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                component="img"
                src={orderedImages[selectedImageIndex]?.url}
                alt={orderedImages[selectedImageIndex]?.caption || 'Objektbild'}
                sx={{ maxWidth: '94vw', maxHeight: '88vh', objectFit: 'contain', display: 'block' }}
              />

              <IconButton
                onClick={() => setLightboxOpen(false)}
                aria-label="Schließen"
                sx={{ position: 'absolute', top: 16, right: 16, color: '#fff', bgcolor: 'rgba(15,23,42,0.6)', '&:hover': { bgcolor: 'rgba(15,23,42,0.85)' } }}
              >
                <CloseIcon />
              </IconButton>

              {orderedImages.length > 1 && (
                <>
                  <IconButton
                    onClick={() => showImage(selectedImageIndex - 1)}
                    aria-label="Vorheriges Bild"
                    sx={{ position: 'absolute', top: '50%', left: 16, transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(15,23,42,0.6)', '&:hover': { bgcolor: 'rgba(15,23,42,0.85)' } }}
                  >
                    <NavigateBeforeIcon fontSize="large" />
                  </IconButton>
                  <IconButton
                    onClick={() => showImage(selectedImageIndex + 1)}
                    aria-label="Nächstes Bild"
                    sx={{ position: 'absolute', top: '50%', right: 16, transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(15,23,42,0.6)', '&:hover': { bgcolor: 'rgba(15,23,42,0.85)' } }}
                  >
                    <NavigateNextIcon fontSize="large" />
                  </IconButton>
                  <Box
                    sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', px: 1.5, py: 0.5, borderRadius: 2, bgcolor: 'rgba(15,23,42,0.7)', color: '#fff', fontSize: 14, fontWeight: 600 }}
                  >
                    {selectedImageIndex + 1} / {orderedImages.length}
                  </Box>
                </>
              )}
            </Box>
          </Dialog>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Eckdaten</Typography>
                    {property?.grundriss_url && (
                      <Button
                        size="small"
                        startIcon={<BlueprintIcon />}
                        href={property.grundriss_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none' }}
                      >
                        Grundriss
                      </Button>
                    )}
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}><Typography>Wohnfläche: {property.wohnflaeche || '-'} m2</Typography></Grid>
                    <Grid item xs={6}><Typography>Nutzfläche: {property.nutzflaeche || '-'} m2</Typography></Grid>
                    <Grid item xs={6}><Typography>Etage: {property.etage || '-'}</Typography></Grid>
                    <Grid item xs={6}><Typography>Terrasse: {property.terrasse_qm || '-'} m2</Typography></Grid>
                    <Grid item xs={6}><Typography>Baujahr: {property.baujahr || '-'}</Typography></Grid>
                    <Grid item xs={6}><Typography>Saniert: {property.saniert_jahr || '-'}</Typography></Grid>
                    <Grid item xs={6}><Typography>Hausgeld: {property.hausgeld_monat || '-'} EUR/Monat</Typography></Grid>
                    <Grid item xs={6}><Typography>Provision: {property.provision_text || '-'}</Typography></Grid>
                    <Grid item xs={6}><Typography>Verfügbarkeit: {property.verfuegbarkeit_text || '-'}</Typography></Grid>
                    <Grid item xs={6}><Typography>Tiefgarage: {property.tiefgarage ? 'Ja' : 'Nein'}</Typography></Grid>
                    <Grid item xs={6}><Typography>Keller: {property.keller ? 'Ja' : 'Nein'}</Typography></Grid>
                    <Grid item xs={6}><Typography>Möbliert: {property.moebliert ? 'Ja' : 'Nein'}</Typography></Grid>
                    {grundstueck.gesamt != null && (
                      <Grid item xs={12}><Typography>Grundstücksfläche (gesamt): {formatGermanNumber(grundstueck.gesamt, 0)} m2</Typography></Grid>
                    )}
                    {grundstueck.anteilText && (
                      <Grid item xs={12}><Typography>Miteigentumsanteil: {grundstueck.anteilText}</Typography></Grid>
                    )}
                    {grundstueck.anteilFlaeche != null && (
                      <Grid item xs={12}>
                        <Box sx={{ mt: 0.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(26,54,93,0.06)' }}>
                          <Typography sx={{ fontWeight: 700, color: '#1a365d' }}>
                            Ihr rechnerischer Grundstücksanteil: ca. {formatGermanNumber(grundstueck.anteilFlaeche, 0)} m2
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            Als Eigentümer erwerben Sie {grundstueck.anteilText}
                            {grundstueck.anteilProzent != null ? ` (≈ ${formatGermanNumber(grundstueck.anteilProzent, 1)} %)` : ''}
                            {' '}des {formatGermanNumber(grundstueck.gesamt, 0)} m2 großen Gesamtgrundstücks – das entspricht rechnerisch rund {formatGermanNumber(grundstueck.anteilFlaeche, 0)} m2 Grundstücksanteil.
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Objektbeschreibung</Typography>
                  <Typography sx={{ whiteSpace: 'pre-line' }}>{property.objektbeschreibung_text || property.beschreibung || '-'}</Typography>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Ausstattung</Typography>
                  {visibleFeatures.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {visibleFeatures.map((item) => (
                        <Chip key={item} label={item} variant="outlined" />
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ whiteSpace: 'pre-line' }}>
                      {property.ausstattung_text || '-'}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Lage</Typography>
                  <Typography sx={{ whiteSpace: 'pre-line' }}>{property.lage_text || '-'}</Typography>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Sonstige</Typography>
                  <Typography sx={{ whiteSpace: 'pre-line' }}>{property.sonstige_text || '-'}</Typography>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Karte</Typography>
                  <Box
                    component="iframe"
                    src={mapEmbedUrl}
                    title="Google Maps Objektlage"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    sx={{ width: '100%', height: 320, border: 0, borderRadius: 2 }}
                  />
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, mt: 3, border: '2px solid #d69e2e' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 800 }}>Energieausweis (Pflichtangaben gemäß GEG)</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}><Typography>Ausweisart: {property.ea_typ || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography>Endenergiekennwert: {property.ea_endenergie || '-'} kWh/(m2*a)</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography>Energieträger: {property.ea_traeger || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography>Baujahr: {property.ea_baujahr || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography>Energieeffizienzklasse: {property.ea_klasse || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography>Gültig bis: {formatGermanDate(property.ea_gueltig_bis)}</Typography></Grid>
                    <Grid item xs={12}><Typography>Warmwasser enthalten: {property.ea_warmwasser_enthalten ? 'Ja' : 'Nein'}</Typography></Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, position: { md: 'sticky' }, top: { md: 24 } }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Exposé anfordern</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    In wenigen Schritten zum vollständigen Exposé. Unverbindlich und kostenlos.
                  </Typography>

                  {errors.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {errors.map((errorText) => <li key={errorText}>{errorText}</li>)}
                      </ul>
                    </Alert>
                  )}

                  {submitResult?.success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Vielen Dank. Ihre Anfrage wurde gespeichert. Wir melden uns in Kürze bei Ihnen.
                    </Alert>
                  )}

                  <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                    {REQUEST_STEPS.map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>

                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <TextField
                      label="Website"
                      value={form.website}
                      onChange={handleFieldChange('website')}
                      inputProps={{ tabIndex: -1, autoComplete: 'off' }}
                      sx={{ display: 'none' }}
                    />

                    {activeStep === 0 && (
                      <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                          So erreichen wir Sie. Pflichtfelder sind nur Name und E-Mail.
                        </Typography>
                        <TextField label="Name" value={form.name} onChange={handleFieldChange('name')} required fullWidth autoFocus />
                        <TextField label="E-Mail" type="email" value={form.email} onChange={handleFieldChange('email')} required fullWidth />
                        <TextField label="Telefon (optional)" value={form.telefon} onChange={handleFieldChange('telefon')} fullWidth />
                        <TextField
                          label="Ihre Nachricht"
                          multiline
                          minRows={4}
                          value={form.nachricht}
                          onChange={handleFieldChange('nachricht')}
                          fullWidth
                          helperText="Bereits für Sie vorausgefüllt – passen Sie den Text gerne an."
                        />
                      </Stack>
                    )}

                    {activeStep === 1 && (
                      <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                          Diese Angaben helfen uns, Ihre Anfrage passend einzuordnen.
                        </Typography>

                        <FormControl required>
                          <FormLabel>Kaufabsicht</FormLabel>
                          <RadioGroup value={form.absicht} onChange={handleFieldChange('absicht')}>
                            <FormControlLabel value="eigennutzung" control={<Radio />} label="Eigennutzung" />
                            <FormControlLabel value="kapitalanlage" control={<Radio />} label="Kapitalanlage" />
                          </RadioGroup>
                        </FormControl>

                        <FormControl required>
                          <FormLabel>Finanzierung</FormLabel>
                          <RadioGroup value={form.finanzierung} onChange={handleFieldChange('finanzierung')}>
                            <FormControlLabel value="zusage" control={<Radio />} label="Finanzierungszusage liegt vor" />
                            <FormControlLabel value="in_klaerung" control={<Radio />} label="Noch in Klärung" />
                            <FormControlLabel value="cashzahlung" control={<Radio />} label="Cashzahlung" />
                          </RadioGroup>
                        </FormControl>

                        <FormControl required>
                          <FormLabel>Zeithorizont</FormLabel>
                          <RadioGroup value={form.zeithorizont} onChange={handleFieldChange('zeithorizont')}>
                            <FormControlLabel value="sofort" control={<Radio />} label="Sofort" />
                            <FormControlLabel value="1_3_monate" control={<Radio />} label="1 bis 3 Monate" />
                            <FormControlLabel value="spaeter" control={<Radio />} label="Später" />
                          </RadioGroup>
                        </FormControl>

                        <Box sx={{ bgcolor: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 2, p: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                            Unterlagen für eine schnelle Finanzierungsentscheidung
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Halten Sie diese Unterlagen idealerweise bereit:
                          </Typography>
                          <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                            <li><Typography variant="body2">Personalausweis / Reisepass</Typography></li>
                            <li><Typography variant="body2">Haushaltsrechnung</Typography></li>
                            <li><Typography variant="body2">Vermögensübersicht</Typography></li>
                            <li><Typography variant="body2">ggf. Immobilienportfolio</Typography></li>
                            <li><Typography variant="body2">letzte drei Gehaltsnachweise</Typography></li>
                            <li><Typography variant="body2">Lohnsteuerbescheinigung vom Vorjahr</Typography></li>
                            <li><Typography variant="body2">Steuerbescheinigung vom Vor-Vorjahr</Typography></li>
                          </Box>
                        </Box>

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={form.finanzierungscheck_confirmed}
                              onChange={handleFieldChange('finanzierungscheck_confirmed')}
                            />
                          }
                          label="Ich habe die Finanzierungs-Checkliste gelesen und meine Finanzierungssituation oben angegeben."
                        />
                      </Stack>
                    )}

                    {activeStep === 2 && (
                      <Stack spacing={2}>
                        <FormControl>
                          <FormLabel>Wunschtermine zur Besichtigung (optional, max. 2)</FormLabel>
                          <Stack>
                            {viewingSlots.length === 0 && (
                              <Typography variant="body2" color="text.secondary">Aktuell sind keine freien Termine hinterlegt – wir stimmen einen Termin individuell mit Ihnen ab.</Typography>
                            )}
                            {viewingSlots.map((slot) => {
                              const formatted = new Date(slot.datum_uhrzeit).toLocaleString('de-DE', {
                                weekday: 'short',
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              });

                              return (
                                <FormControlLabel
                                  key={slot.id}
                                  control={(
                                    <Checkbox
                                      checked={selectedSlots.includes(slot.id)}
                                      onChange={() => toggleSlot(slot.id)}
                                      inputProps={{ 'aria-label': `Termin ${formatted}` }}
                                    />
                                  )}
                                  label={formatted}
                                />
                              );
                            })}
                          </Stack>
                        </FormControl>

                        <Divider />

                        <Box sx={{ bgcolor: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 2, p: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Provisionshinweis (Maklercourtage)</Typography>
                          <Typography variant="body2">
                            {property.provision_text
                              ? property.provision_text
                              : 'Die Höhe der Maklercourtage wird Ihnen vor einer Besichtigung transparent mitgeteilt.'}
                          </Typography>
                        </Box>

                        <FormControlLabel
                          control={<Checkbox checked={form.maklerbedingungen_consent} onChange={handleFieldChange('maklerbedingungen_consent')} required />}
                          label={(
                            <Typography variant="body2">
                              Ich habe den oben genannten Provisionshinweis{property.provision_text ? ` (${property.provision_text})` : ''} zur Kenntnis genommen und erkenne an, dass im Erfolgsfall – also bei Abschluss eines Kaufvertrags über das nachgewiesene oder vermittelte Objekt – ein Maklerprovisionsanspruch gemäß §§ 652 ff. BGB entsteht. Die Maklertätigkeit (Nachweis/Vermittlung) beginnt mit der Übersendung des Exposés bzw. der angeforderten Unterlagen.
                            </Typography>
                          )}
                        />

                        <FormControlLabel
                          control={<Checkbox checked={form.vorzeitiger_beginn_consent} onChange={handleFieldChange('vorzeitiger_beginn_consent')} required />}
                          label={(
                            <Typography variant="body2">
                              Ich verlange ausdrücklich, dass die Maklertätigkeit (Nachweis/Vermittlung und Exposé-Freischaltung) bereits vor Ablauf der Widerrufsfrist beginnt (§ 357 Abs. 8 BGB).
                            </Typography>
                          )}
                        />

                        <FormControlLabel
                          control={<Checkbox checked={form.widerrufsbelehrung_consent} onChange={handleFieldChange('widerrufsbelehrung_consent')} required />}
                          label={(
                            <Typography variant="body2">
                              Ich bestätige, dass ich die Widerrufsbelehrung erhalten und zur Kenntnis genommen habe.
                            </Typography>
                          )}
                        />

                        <FormControlLabel
                          control={<Checkbox checked={form.dsgvo_consent} onChange={handleFieldChange('dsgvo_consent')} required />}
                          label={(
                            <Typography variant="body2">
                              Ich willige ein, dass meine angegebenen Daten (Name, E-Mail, Telefon und Nachricht) zur Bearbeitung meiner Anfrage und zur Kontaktaufnahme gespeichert und verarbeitet werden (Art. 6 Abs. 1 lit. a und b DSGVO). Die Daten werden gelöscht, sobald sie für die Anfrage nicht mehr benötigt werden und kein Vertragsverhältnis zustande kommt, spätestens nach 6 Monaten automatisch. Diese Einwilligung kann ich jederzeit mit Wirkung für die Zukunft widerrufen. Details in der{' '}
                              <Link component={RouterLink} to="/datenschutz">Datenschutzerklärung</Link>.
                            </Typography>
                          )}
                        />
                      </Stack>
                    )}

                    <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={handleBack}
                        disabled={activeStep === 0 || submitting}
                        sx={{ flex: '0 0 auto' }}
                      >
                        Zurück
                      </Button>
                      {activeStep < REQUEST_STEPS.length - 1 ? (
                        <Button type="button" variant="contained" size="large" onClick={handleNext} fullWidth>
                          Weiter
                        </Button>
                      ) : (
                        <Button type="submit" variant="contained" size="large" disabled={submitting} fullWidth>
                          {submitting ? 'Wird gesendet...' : 'Exposé anfordern'}
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  {submitResult?.gatedDownloads?.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Ihre freigeschalteten Dokumente</Typography>
                      <Stack spacing={1}>
                        {submitResult.gatedDownloads.map((doc) => (
                          <Link key={doc.url} href={getApiUrl(doc.url)} target="_blank" rel="noopener noreferrer">
                            {doc.label}
                          </Link>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {documents.length > 0 && (
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>Dokumente</Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Exposé, Energieausweis und Teilungserklärung werden nach erfolgreicher Anfrage freigeschaltet.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {documents.map((doc) => (
                    <Chip key={doc.id} label={doc.label} color={doc.gated ? 'warning' : 'default'} variant="outlined" />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default PropertyLandingPage;
